interface IActionHistory<T> {
    undoAndGetPreviousStateEntry(): IStateActionEntry<T> | null;
    redoAndGetNextStateEntry(): IStateActionEntry<T> | null;

    pushStateEntry(entry: IStateActionEntry<T>): void;
    pushStateEntryFromAction(action: StateAction<T>): void;

    canUndo(): boolean;
    canRedo(): boolean;
}

interface IStateActionEntry<T> {
    actions: Array<StateAction<T>>;
}

export interface IStateAddedAction<T> {
    type: 'added';
    after: T;
}

export interface IStateDeletedAction<T> {
    type: 'removed';
    before: T;
}

export interface IStateModifiedAction<T> {
    type: 'modified';
    before: T;
    after: T;
}

export type StateAction<T> =
    | IStateAddedAction<T>
    | IStateDeletedAction<T>
    | IStateModifiedAction<T>;

interface Options {
    shouldCloneActions?: boolean;
    sizeLimit?: number;
}

export class StateActionHistory<T> implements IActionHistory<T> {
    private readonly _entries: Array<IStateActionEntry<T>>;
    private _currentActionPointer: number;
    private _shouldCloneActions: boolean;
    private _sizeLimit: number;
    private _pendingActions: Array<StateAction<T>>;

    constructor({ shouldCloneActions = false, sizeLimit = 25 }: Options) {
        this._entries = [];
        this._currentActionPointer = -1;
        this._shouldCloneActions = shouldCloneActions;
        this._sizeLimit = sizeLimit;
        this._pendingActions = [];
    }

    public get sizeLimit(): number {
        return this._sizeLimit;
    }

    public get size(): number {
        return this._entries.length;
    }

    public get currentActionPointer(): number {
        return this._currentActionPointer;
    }

    public get shouldCloneActions(): boolean {
        return this._shouldCloneActions;
    }

    public get entries(): ReadonlyArray<IStateActionEntry<T>> {
        return this._entries;
    }

    public canUndo(): boolean {
        return this._currentActionPointer >= 0;
    }

    public canRedo(): boolean {
        return this._currentActionPointer < this._entries.length - 1;
    }

    public undoAndGetPreviousStateEntry(): IStateActionEntry<T> | null {
        if (!this.canUndo()) {
            return null;
        }
        const entry = this._entries[this.currentActionPointer];
        this._currentActionPointer--;
        return entry;
    }

    public redoAndGetNextStateEntry(): IStateActionEntry<T> | null {
        if (!this.canRedo()) {
            return null;
        }
        this._currentActionPointer++;
        const entry = this._entries[this.currentActionPointer];
        return entry;
    }

    public pushStateEntry(entry: IStateActionEntry<T>): void {
        this._currentActionPointer++;
        const maxHistoryIndex = this._sizeLimit - 1;

        if (this.shouldCloneActions) {
            entry.actions = JSON.parse(JSON.stringify(entry.actions));
        }

        this._entries.splice(this.currentActionPointer);
        const spliceCount = this.currentActionPointer - maxHistoryIndex;
        if (spliceCount > 0) {
            this._entries.splice(0, spliceCount);
        }
        this._entries.push(entry);

        this._currentActionPointer = Math.min(
            this.currentActionPointer,
            maxHistoryIndex,
        );
    }

    public pushStateEntryFromAction(action: StateAction<T>): void {
        return this.pushStateEntry({
            actions: [action],
        });
    }

    public enqueueAction(action: StateAction<T>): void {
        this._pendingActions.push(action);
    }

    public clearQueuedActions(): void {
        this._pendingActions = [];
    }

    public commit(): boolean {
        if (this._pendingActions.length > 0) {
            this.pushStateEntry({ actions: this._pendingActions });
            this._pendingActions = [];
            return true;
        }
        return false;
    }
}
